import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import { listStudiesInfiniteOptions } from '@/hooks/useHomeWork';
import type {
  ListStudiesResponse,
  StudyResponse,
} from '@/api/generated/types.gen';

/**
 * Page size for the SimOps / EvalTool study libraries.
 *
 * Smaller than the home page's `HOME_PAGE_LIMIT` (100): those cards sort over
 * one wide page, while these tables page as the reader scrolls. Owned here so
 * the loader and both landings share one cache entry — two limits would split
 * it and poll the same list twice.
 */
export const STUDIES_PAGE_LIMIT = 20;

/**
 * The cursor-paginated study list both product libraries show.
 *
 * A function, not a module-scope const: generated query options capture the
 * client `baseUrl` at creation time, and route modules evaluate (via
 * `routeTree.gen`) before `generatedClient.ts` configures it. Both landings
 * build the same options, so they share one cache entry and moving between
 * `/simops` and `/evaltool` refetches nothing.
 */
export const studyListInfiniteOptions = () =>
  listStudiesInfiniteOptions(STUDIES_PAGE_LIMIT);

export function flattenStudyPages(
  data: InfiniteData<ListStudiesResponse> | undefined,
): StudyResponse[] {
  return data?.pages.flatMap((page) => page.items) ?? [];
}

/**
 * The cache warm both landing routes do, so navigation lands on content rather
 * than a spinner. Best-effort: the components keep their own `useInfiniteQuery`,
 * polling, retries and error UI, and a failed warm falls through to those.
 *
 * The study list only. It used to await the geometries list as well, because
 * the preview tiles read their cache-version token off it (ENG-2032) — a
 * request the study list had no other use for, blocking every hard load. The
 * token now arrives on the study row itself as `preview_diagnosis_at`
 * (ENG-2049), so that warm and its cost went together (ENG-2063).
 *
 * Shared rather than copied so `/simops` and `/evaltool` cannot diverge on
 * what they warm.
 */
export async function studyListLoader(queryClient: QueryClient): Promise<void> {
  await queryClient
    .ensureInfiniteQueryData(studyListInfiniteOptions())
    .catch(() => undefined);
}
